import { useNavigate } from 'react-router-dom';
import { Banner, ProgressBar, InlineStack, Text, Box } from '@shopify/polaris';
import { AlertTriangleIcon, ClockIcon } from '@shopify/polaris-icons';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { useBillingUsage } from '../hooks/api';

export function UsageWarningBanner() {
  const t = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { data: usage, isLoading } = useBillingUsage();

  if (isLoading || !usage) {
    return null;
  }

  const { events, warnings, trial } = usage;

  // Show trial ending warning
  if (trial.active && trial.daysRemaining <= 7) {
    return (
      <Banner
        tone="info"
        icon={ClockIcon}
        action={{
          content: t.billing.upgradeNow,
          onAction: () => navigate('/billing'),
        }}
      >
        <Text as="p">
          {t.billing.trialEndsIn} <strong>{trial.daysRemaining}</strong> {t.billing.days}. {t.billing.warningTrialEnding}
        </Text>
      </Banner>
    );
  }

  // Show over limit warning (critical)
  if (warnings.isOverLimit) {
    return (
      <Banner
        tone="critical"
        icon={AlertTriangleIcon}
        action={{
          content: t.billing.upgradeNow,
          onAction: () => navigate('/billing'),
        }}
      >
        <Box paddingBlockEnd="200">
          <Text as="p">{t.billing.warningOverLimit}</Text>
        </Box>
        <InlineStack gap="200" blockAlign="center">
          <Box width="200px">
            <ProgressBar progress={100} tone="critical" size="small" />
          </Box>
          <Text as="span" tone="subdued" variant="bodySm">
            {events.used.toLocaleString(localeMap[language])} / {typeof events.limit === 'number' ? events.limit.toLocaleString(localeMap[language]) : events.limit}
          </Text>
        </InlineStack>
      </Banner>
    );
  }

  // Show near limit warning
  if (warnings.isNearLimit) {
    return (
      <Banner
        tone="warning"
        icon={AlertTriangleIcon}
        action={{
          content: t.billing.upgradeNow,
          onAction: () => navigate('/billing'),
        }}
      >
        <Box paddingBlockEnd="200">
          <Text as="p">{t.billing.warningNearLimit}</Text>
        </Box>
        <InlineStack gap="200" blockAlign="center">
          <Box width="200px">
            <ProgressBar progress={events.percentage} tone="highlight" size="small" />
          </Box>
          <Text as="span" tone="subdued" variant="bodySm">
            {events.used.toLocaleString(localeMap[language])} / {typeof events.limit === 'number' ? events.limit.toLocaleString(localeMap[language]) : events.limit} ({events.percentage.toFixed(0)}%)
          </Text>
        </InlineStack>
      </Banner>
    );
  }

  return null;
}
